import React, { useEffect, useState } from 'react';
import { FaUnity, FaExternalLinkAlt, FaTools, FaShoppingCart, FaGraduationCap, FaClock } from 'react-icons/fa';
import SEO from '../components/SEO';

const Assets: React.FC = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX - window.innerWidth / 2,
        y: e.clientY - window.innerHeight / 2,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const items = [
    {
      name: 'Unity Asset Store',
      description: 'Find our high-quality 3D models, tools, and environments on the Unity Asset Store.',
      icon: FaUnity,
      link: '#', // Add your Unity Asset Store publisher link here
      color: 'from-blue-500 to-indigo-600',
      hoverColor: 'hover:shadow-blue-500/20 hover:border-blue-500/50',
      isComingSoon: false,
    },
    {
      name: 'Fab',
      description: 'Discover our premium assets for Unreal Engine and UEFN on Epic Games Fab.',
      icon: FaShoppingCart,
      link: '#', // Add your Fab publisher link here
      color: 'from-purple-500 to-pink-600',
      hoverColor: 'hover:shadow-purple-500/20 hover:border-purple-500/50',
      isComingSoon: false,
    },
    {
      name: 'RedRender Courses',
      description: 'Master game development and 3D modeling with our upcoming professional courses.',
      icon: FaGraduationCap,
      link: '#',
      color: 'from-green-500 to-emerald-600',
      hoverColor: 'hover:shadow-green-500/20 hover:border-green-500/50',
      isComingSoon: true,
    },
  ];

  return (
    <>
      <SEO
        title="Assets Store - RedRender"
        description="Browse and purchase high-quality 3D assets, tools, and environments by RedRender on Unity Asset Store and Fab."
        path="/assets"
        keywords={['RedRender assets', '3D models', 'Unity Asset Store', 'Fab', 'Epic Games', 'game assets']}
      />
      
      <section className="relative min-h-screen pt-32 pb-20 flex flex-col items-center overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black">
        {/* Animated Gradient Orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className="absolute w-96 h-96 bg-red-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"
            style={{
              top: '10%',
              left: '10%',
              transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`,
            }}
          ></div>
          <div 
            className="absolute w-96 h-96 bg-red-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"
            style={{
              top: '60%',
              right: '10%',
              animationDelay: '2s',
              transform: `translate(${mousePosition.x * -0.02}px, ${mousePosition.y * -0.02}px)`,
            }}
          ></div>
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px] pointer-events-none"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center max-w-4xl">
          {/* Work in Progress Badge */}
          <div className="inline-flex items-center space-x-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full px-5 py-2 backdrop-blur-sm mb-8 animate-slide-up">
            <FaTools className="text-yellow-500 animate-pulse" />
            <span className="text-yellow-500 font-semibold text-sm font-display tracking-wider uppercase">Work in Progress</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight font-display text-white mb-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
            Our Digital
            <span className="block mt-2 bg-gradient-to-r from-red-500 via-red-400 to-red-500 bg-clip-text text-transparent bg-[length:200%_100%] animate-gradient">
              Asset Store
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-gray-400 mb-12 leading-relaxed animate-slide-up" style={{ animationDelay: '0.2s' }}>
            We are currently building our centralized asset platform. In the meantime, you can purchase our high-quality 3D models, environments, and tools from our official publisher pages.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mt-12 relative perspective-1000">
            {items.map((item, index) => {
              const CardWrapper = item.isComingSoon ? 'div' : 'a';
              return (
              <CardWrapper
                key={item.name}
                href={item.isComingSoon ? undefined : item.link}
                target={item.isComingSoon ? undefined : "_blank"}
                rel={item.isComingSoon ? undefined : "noopener noreferrer"}
                className={`group relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl p-8 rounded-3xl border border-white/10 transition-all duration-500 flex flex-col items-center text-center ${
                  item.isComingSoon 
                    ? 'opacity-80 cursor-default' 
                    : `transform hover:-translate-y-3 hover:shadow-2xl ${item.hoverColor} cursor-pointer`
                }`}
                style={{ 
                  animationDelay: `${0.3 + index * 0.1}s`,
                }}
              >
                {/* Glow Effect */}
                {!item.isComingSoon && (
                  <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-5 rounded-3xl transition-all duration-500`}></div>
                )}
                
                {/* WIP Badge for Coming Soon */}
                {item.isComingSoon && (
                  <div className="absolute top-4 right-4 bg-yellow-500/20 border border-yellow-500/30 text-yellow-500 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5">
                    <FaClock /> W.I.P
                  </div>
                )}
                
                <div className={`w-20 h-20 rounded-2xl mb-6 flex items-center justify-center bg-gradient-to-br ${item.color} shadow-lg ${item.isComingSoon ? '' : 'group-hover:scale-110'} transition-transform duration-300`}>
                  <item.icon className="text-4xl text-white" />
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-3 font-display">{item.name}</h3>
                <p className="text-gray-400 text-sm mb-6 flex-grow">{item.description}</p>
                
                <div className={`mt-auto inline-flex items-center space-x-2 font-medium text-sm transition-colors ${
                  item.isComingSoon ? 'text-yellow-500/80' : 'text-white group-hover:text-red-400'
                }`}>
                  <span>{item.isComingSoon ? 'Coming Soon...' : 'Visit Store'}</span>
                  {!item.isComingSoon && <FaExternalLinkAlt className="text-xs group-hover:translate-x-1 transition-transform" />}
                </div>
              </CardWrapper>
            )})}
          </div>
        </div>
      </section>
    </>
  );
};

export default Assets;
